import { z } from "zod";
import type { ConfigTypeId } from "../registry";
import { defineStepConfig } from "../registry";

/**
 * Stable type identifier for the form trigger step.
 *
 * Imported by backend orchestration code (to detect entry steps that are
 * auto-completed) and by the public form submission path (to find the
 * trigger node in a pinned workflow definition).
 */
export const FORM_TRIGGER_STEP_TYPE = "form_trigger" satisfies ConfigTypeId;

/**
 * Field types a form trigger can collect. Every type maps to exactly one
 * output socket type — see `formFieldTypeToSocketType` in
 * `utils/formTriggerOutputSockets.ts`.
 */
export const FORM_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "boolean",
  "select",
  "date",
  "file",
  "customer",
  "supplier",
] as const;

export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

/**
 * Entity-picker field types: the public form renders a searchable dropdown of
 * the organization's records and the submitted value flows through as an
 * entity reference.
 *
 * These are SENSITIVE on public forms — serving the picker options to an
 * anonymous visitor exposes the organization's customer/supplier list. The
 * editor warns (and asks for confirmation) when a form combines these field
 * types with `requireAuth: false`.
 */
export const FORM_ENTITY_FIELD_TYPES = ["customer", "supplier"] as const;

export type FormEntityFieldType = (typeof FORM_ENTITY_FIELD_TYPES)[number];

export function isFormEntityFieldType(
  fieldType: string,
): fieldType is FormEntityFieldType {
  return (FORM_ENTITY_FIELD_TYPES as readonly string[]).includes(fieldType);
}

/** True when any configured field exposes org data through its picker options. */
export function formFieldsContainEntityPickers(
  fields: ReadonlyArray<{ type?: string }> | undefined,
): boolean {
  return (fields ?? []).some(
    (f) => typeof f?.type === "string" && isFormEntityFieldType(f.type),
  );
}

/**
 * Field ids double as output socket ids, so they follow the same shape as
 * `http_trigger` schema field names (snake_case identifiers).
 */
export const FORM_FIELD_ID_REGEX = /^[a-z][a-z0-9_]*$/;

/**
 * Public URL slugs: lowercase kebab-case, 3–100 chars. Slugs are matched
 * case-insensitively at submission time and must be globally unique across
 * pinned workflows (the editor checks availability while configuring).
 */
export const FORM_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const FORM_SLUG_MIN_LENGTH = 3;
export const FORM_SLUG_MAX_LENGTH = 100;

/**
 * Socket ids reserved by `form_trigger`; cannot be used as field ids.
 * Defined here (not in the socket generator) so the config schema can reject
 * collisions without a circular import.
 */
export const FORM_TRIGGER_RESERVED_OUTPUT_SOCKET_IDS = [
  "submission",
  "submittedBy",
  "submittedAt",
] as const;

export const formFieldSchema = z.object({
  /** Socket id + multipart field name. Snake_case identifier. */
  id: z.string().regex(FORM_FIELD_ID_REGEX),
  /** Human-facing label rendered on the public form. */
  label: z.string().min(1),
  type: z.enum(FORM_FIELD_TYPES),
  required: z.boolean().default(false),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  /** Choices for `select` fields; ignored for other types. */
  options: z.array(z.string()).optional(),
});

export type FormTriggerField = z.infer<typeof formFieldSchema>;

const formTriggerConfigSchema = z
  .object({
    /** Readable URL slug — the form is served at `/forms/<slug>`. */
    slug: z
      .string()
      .min(FORM_SLUG_MIN_LENGTH)
      .max(FORM_SLUG_MAX_LENGTH)
      .regex(FORM_SLUG_REGEX)
      .describe("Readable URL slug; the form is served at /forms/<slug>."),
    /** Title shown at the top of the public form. */
    title: z.string().default(""),
    /** Optional description rendered under the title. */
    description: z.string().optional(),
    /** Ordered field definitions; each becomes an output socket. */
    fields: z.array(formFieldSchema).default([]),
    /**
     * When true (default) submitters must be signed in and belong to the
     * workflow's organization. When false anyone with the link can submit.
     */
    requireAuth: z.boolean().default(true),
    /**
     * Restrict submissions to these users (only enforced when requireAuth is
     * true). Empty + empty groups = any member of the organization.
     */
    allowedUserIds: z.array(z.string().uuid()).default([]),
    /**
     * Restrict submissions to members of these user groups (only enforced
     * when requireAuth is true).
     */
    allowedUserGroupIds: z.array(z.string().uuid()).default([]),
  })
  .superRefine((config, ctx) => {
    const seen = new Set<string>();
    for (const [index, field] of config.fields.entries()) {
      if (
        (FORM_TRIGGER_RESERVED_OUTPUT_SOCKET_IDS as readonly string[]).includes(
          field.id,
        )
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["fields", index, "id"],
          message: `Field id "${field.id}" is reserved`,
        });
      }
      if (seen.has(field.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["fields", index, "id"],
          message: `Duplicate field id "${field.id}"`,
        });
      }
      seen.add(field.id);
    }
  });

export const formTriggerStep = defineStepConfig({
  type: FORM_TRIGGER_STEP_TYPE,
  name: "Form Trigger",
  description:
    "Entry point for workflows triggered by a hosted form at a unique public URL. Supports text, number, date, select and file inputs, optional sign-in, and per-user/per-team access restrictions.",
  category: "Trigger",

  inputSchema: z.never(),

  // Output sockets are dynamic: one per configured field plus the reserved
  // `submission` / `submittedBy` / `submittedAt` sockets. See
  // `generateFormTriggerOutputSockets` in `@erp/workflows`.
  outputSchema: z.object({
    submission: z.record(z.string(), z.unknown()),
    submittedBy: z.string().nullable(),
    submittedAt: z.string(),
  }),

  configSchema: formTriggerConfigSchema,
  emits: [] as const,
});

export type FormTriggerConfig = z.infer<typeof formTriggerStep.configSchema>;
